import { GridColDef, GridSingleSelectColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { DotNestedKeys } from "src/types/objects";
import {
	formatIsoString,
	isValidDate,
	isValidDateString,
	isValidISOString,
	SupportedDateFormats,
} from "src/utils/dayjsUtils.ts";
import i18n from "i18next";
import {
	GridEditBooleanCell,
	GridEditDateCell,
	GridEditInputCell,
	GridEditSingleSelectCell,
	GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import { logError } from "src/errorHandling/errorLogging";
import dayjs, { Dayjs } from "dayjs";
import {
	ColumnValidationProp,
	columnValidationProps,
} from "src/components/common/dataGrid/crud/validation/columnValidation.tsx";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/pro-regular-svg-icons";

export interface AavoColumnBaseProps<RowData extends object, V>
	extends ColumnValidationProp<RowData, V> {
	field: DotNestedKeys<RowData>;
}

export const customColumn = <RowData extends object>(
	props: Omit<GridColDef<RowData>, "field"> & AavoColumnBaseProps<RowData, string>,
): GridColDef<RowData> => props;

export const textColumn = <RowData extends object>(
	props: Omit<GridColDef<RowData>, "type" | "field"> & AavoColumnBaseProps<RowData, string>,
): GridColDef<RowData> => ({
	...props,
	...columnValidationProps(props, GridEditInputCell),
	type: "string",
});

export const singleSelectColumn = <RowData extends object, V = any>(
	props: Omit<GridColDef<RowData>, "type" | "field" | "valueGetter" | "valueOptions"> &
		AavoColumnBaseProps<RowData, V> & {
			// valueOptions is redeclared, because Webstorm has problems detecting it from GridColDef
			valueOptions: GridSingleSelectColDef["valueOptions"];
		},
): GridColDef<RowData> => {
	return {
		...props,
		...columnValidationProps(props, GridEditSingleSelectCell),
		type: "singleSelect",
		valueGetter: ({ value }) => {
			if (value === null || value === undefined) {
				return "";
			} else {
				return value;
			}
		},
	};
};

export const enumColumn = <RowData extends object, TEnum extends string>(
	props: Omit<GridColDef<RowData>, "type" | "field" | "valueGetter"> & {
		field: DotNestedKeys<RowData>;
		enumLabels: { [key in TEnum]: string };
	} & ColumnValidationProp<RowData, TEnum>,
): GridColDef<RowData> => {
	return singleSelectColumn({
		...props,
		...columnValidationProps(props, GridEditSingleSelectCell),
		valueOptions: Object.keys(props.enumLabels).map((key) => ({
			value: key,
			label: props.enumLabels[key as TEnum],
		})),
	});
};

export const booleanColumn = <RowData extends object>(
	props: Omit<GridColDef<RowData>, "type" | "field"> & AavoColumnBaseProps<RowData, boolean>,
): GridColDef<RowData> => {
	return {
		...props,
		type: "boolean",
		...columnValidationProps(props, GridEditBooleanCell),
	};
};

export const integerColumn = <RowData extends object>(
	props: Omit<GridColDef<RowData>, "type" | "field" | "align" | "headerAlign"> &
		AavoColumnBaseProps<RowData, number>,
): GridColDef<RowData> => {
	return {
		...props,
		...columnValidationProps(props, GridEditInputCell),
		type: "number",
		align: "left",
		headerAlign: "left",
		valueFormatter: (params) => {
			if (params.value === null || params.value === undefined) {
				return "";
			} else {
				return params.value.toLocaleString(i18n.language);
			}
		},
	};
};

export const floatColumn = <RowData extends object>(
	props: Omit<GridColDef<RowData>, "type" | "field" | "align" | "headerAlign"> &
		AavoColumnBaseProps<RowData, number>,
): GridColDef<RowData> => {
	return {
		...props,
		...columnValidationProps(props, GridEditInputCell),
		type: "number",
		align: "left",
		headerAlign: "left",
		valueFormatter: (params) => {
			if (params.value === null || params.value === undefined) {
				return "";
			} else {
				return params.value.toLocaleString(i18n.language);
			}
		},
	};
};

export const dateTimeColumn = dateColumnFactory("L LT");

export const dateColumn = dateColumnFactory("L");

function dateColumnFactory(format: SupportedDateFormats) {
	return <RowData extends object>(
		props: Omit<
			GridColDef<RowData>,
			"type" | "valueGetter" | "valueParser" | "valueFormatter"
		> &
			ColumnValidationProp<RowData, Dayjs>,
	): GridColDef<RowData> => {
		return {
			...props,
			...columnValidationProps(props, GridEditDateCell),
			type: "date",
			valueGetter: dateValueGetter,
			valueParser: dateValueParser,
			valueFormatter: (params) => {
				const value: any = params.value;
				if (value == undefined) {
					return "";
				} else if (isValidDate(value)) {
					return formatIsoString(value.toISOString(), format);
				} else {
					return i18n.t("invalid_date");
				}
			},
		};
	};
}

const dateValueGetter = <RowData extends object>(
	params: GridValueGetterParams<RowData>,
): Date | undefined => {
	const value = params.value as unknown;
	if (value == null) return undefined;
	if (isValidDate(value)) return value;
	else if (typeof value === "string" && (isValidISOString(value) || isValidDateString(value))) {
		const dayjsDate = dayjs(value);
		return dayjsDate.toDate();
	} else {
		logError(`Invalid date value in data grid ${value}`);
		return undefined;
	}
};

const dateValueParser = (value: any): any => {
	if (typeof value === "string" || value === undefined) {
		return value;
	} else {
		return value.toISOString();
	}
};

export const coloredBooleanColumn = <RowData extends object>(
	props: Omit<GridColDef<RowData>, "type" | "field"> & AavoColumnBaseProps<RowData, boolean>,
): GridColDef<RowData> =>
	booleanColumn({
		renderCell: ({ value }) => {
			return value ? (
				<FontAwesomeIcon icon={faCheck} color={"green"} size={"lg"} />
			) : (
				<FontAwesomeIcon icon={faXmark} color={"red"} size={"lg"} />
			);
		},
		...props,
	});

export const iconColumn = <RowData extends object>({
	getIconProps,
	...other
}: Omit<GridColDef<RowData>, "renderCell"> & {
	getIconProps: (rowData: RowData) => FontAwesomeIconProps | undefined;
}): GridColDef<RowData> => ({
	renderCell: ({ row }) => {
		const iconResult = getIconProps(row);
		if (iconResult === undefined) return null;
		return <FontAwesomeIcon {...iconResult} />;
	},
	width: 10,
	align: "center",
	...other,
});
